import { Button } from '@creditornot/cb-button';
import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { BasketDiscountType, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import WoltModal from 'common/components/WoltModal';

import { getUpperLimitOptionFromValue } from './utils';
import { BasketDiscountFormValues } from '../../../types';
import { CampaignsTargetContent } from '../../../utils/telemetry/constants';
import {
  telemetryNewCampaignInteractionClickCampaignRules,
  telemetryUpdateCampaignInteractionClickCampaignUpdate,
} from '../../../utils/telemetry/telemetry';
import UnsavedChangesModal from '../../modals/UnsavedChangesModal/UnsavedChangesModal';
import { DiscountAmountDetails, DiscountAmountDetailsProps } from '../DiscountAmountDetails';
import { DiscountAmountFormModal } from '../DiscountAmountFormModal/DiscountAmountFormModal';
import { NO_UPPER_LIMIT_OPTION } from '../constants';
import { DiscountAmountFormValues } from '../types';

export type DiscountAmountFormItemProps = {
  isDisabled?: boolean;
  discountType: BasketDiscountType;
  discountValue: BasketDiscountFormValues['discountValue'];
  discountValueOptions: {
    [BasketDiscountType.AMOUNT]: number[];
    [BasketDiscountType.PERCENTAGE]: number[];
  };
  recommendedDiscountValue: {
    [BasketDiscountType.AMOUNT]: number;
    [BasketDiscountType.PERCENTAGE]: number;
  };
  upperLimitOptions: number[];
  recommendedUpperLimit: number | null;
  campaignType: CampaignType;
  onUpdate: (values: { discountAmount: number; upperLimit: number | null }) => void;
  isNewCampaignRoute?: boolean;
} & Omit<DiscountAmountDetailsProps, 'discountAmount'>;

export const DiscountAmountFormItem = ({
  onUpdate,
  discountValue,
  minBasketSize,
  discountType,
  discountValueOptions,
  recommendedDiscountValue,
  upperLimitOptions,
  recommendedUpperLimit,
  isDisabled,
  isNewCampaignRoute,
  campaignType,
  ...props
}: DiscountAmountFormItemProps) => {
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const modalDefaultValues = useMemo<DiscountAmountFormValues>(
    () => ({
      discountAmount: discountValue[discountType] || recommendedDiscountValue[discountType],
      upperLimit: getUpperLimitOptionFromValue(discountValue.upperLimit, recommendedUpperLimit),
    }),
    [discountValue, discountType, recommendedDiscountValue, recommendedUpperLimit],
  );

  const handleChangeClick = () => {
    setIsModalOpen(true);

    if (isNewCampaignRoute) {
      telemetryNewCampaignInteractionClickCampaignRules(CampaignsTargetContent.NEW.CHANGE_DISCOUNT, campaignType);
    } else {
      telemetryUpdateCampaignInteractionClickCampaignUpdate(CampaignsTargetContent.EDIT.CHANGE_DISCOUNT, campaignType);
    }
  };

  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsModalOpen(false);
    }
  };

  const handleSubmit = (formData: DiscountAmountFormValues) => {
    const updatedData = {
      ...formData,
      upperLimit: formData.upperLimit === NO_UPPER_LIMIT_OPTION ? null : formData.upperLimit,
    };

    onUpdate(updatedData);
    handleCloseModals();
  };

  return (
    <>
      <Form<DiscountAmountFormValues> onSubmit={handleSubmit} initialValues={modalDefaultValues}>
        {({ handleSubmit, dirty: isFormDirty, form }) => {
          return (
            <WoltModal isOpen={isModalOpen} onRequestClose={() => handleCloseModals(isFormDirty)}>
              <DiscountAmountFormModal
                discountType={discountType}
                discountValueOptions={discountValueOptions}
                recommendedDiscountValue={recommendedDiscountValue}
                upperLimitOptions={upperLimitOptions}
                recommendedUpperLimit={recommendedUpperLimit}
                minBasketSize={minBasketSize}
                campaignType={campaignType}
                onCloseClick={() => handleCloseModals(isFormDirty)}
                onUpdate={handleSubmit}
                isNewCampaignRoute={isNewCampaignRoute}
              />

              <UnsavedChangesModal
                isOpen={isFormDirty && isUnsavedChangesModalOpen}
                onDropChanges={() => {
                  handleCloseModals();
                  form.reset();
                }}
                onClose={() => setIsUnsavedChangesModalOpen(false)}
              />
            </WoltModal>
          );
        }}
      </Form>

      <DiscountAmountDetails
        data-testid={props['data-testid']}
        action={
          <Button variant="lightBlue" size="small" onClick={handleChangeClick} disabled={isDisabled}>
            <FormattedMessage id="campaigns.form.discount-amount.change-button" />
          </Button>
        }
        discountAmount={discountValue[discountType] || recommendedDiscountValue[discountType]}
        discountType={discountType}
        minBasketSize={minBasketSize}
      />
    </>
  );
};
